/**
 * Configurazione runtime. Base URL dell'API, in quest'ordine:
 *   1. query string `?api=https://…` (poi salvata in localStorage: ai riavvii senza query vale la copia salvata)
 *   2. localStorage (impostata al punto 1)
 *   3. variabile di build VITE_API_BASE
 *   4. stesso origin della pagina
 * `?debug=1` abilita overlay, log ed `window.__player`.
 * `?provision=/provision.json` (solo query, mai salvato): endpoint del server locale (Raspberry kiosk) dove il
 * player tiene una copia del token; accettato solo se ha lo stesso origin della pagina.
 */

export const STORAGE_KEY_API = 'player.apiBase';

export interface Config {
  apiBase: string;
  debug: boolean;
  /** Override dell'intervallo di heartbeat (ms); solo con debug. */
  hbMs?: number;
  /** `?maxh=1080`: tetto al lato corto richiesto al server (es. Raspberry Pi: niente 4K HEVC anche su schermo 4K). */
  maxHeight?: 1080 | 2160;
  /** URL assoluto (stesso origin della pagina) dell'endpoint di provisioning del token; solo da `?provision=`. */
  provisionUrl?: string;
}

export interface ConfigEnv {
  search: string;
  origin: string;
  buildApiBase?: string;
  isDev?: boolean;
  storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> | null;
}

function normalize(url: string): string | null {
  try {
    const u = new URL(url);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null;
    return u.origin + u.pathname.replace(/\/+$/, '');
  } catch {
    return null;
  }
}

function safe<T>(fn: () => T): T | null {
  try {
    return fn();
  } catch {
    return null;
  }
}

/** `?hb=5` (solo con debug): heartbeat e flush dei play ogni 5 s, per i test e2e. */
export function resolveConfig(env: ConfigEnv): Config {
  const cfg = resolveBase(env);
  const hb = Number(new URLSearchParams(env.search).get('hb'));
  if (cfg.debug && Number.isFinite(hb) && hb >= 1 && hb <= 3600) cfg.hbMs = Math.round(hb * 1000);
  const maxh = new URLSearchParams(env.search).get('maxh');
  if (maxh === '1080' || maxh === '2160') cfg.maxHeight = Number(maxh) as 1080 | 2160;
  const provision = resolveProvision(env);
  if (provision) cfg.provisionUrl = provision;
  return cfg;
}

/** `?provision=`: relativo o assoluto, ma solo stesso origin e http/https (il token non deve andare ad altri host). */
function resolveProvision(env: ConfigEnv): string | undefined {
  const q = new URLSearchParams(env.search).get('provision');
  if (!q) return undefined;
  try {
    const base = new URL(env.origin);
    const u = new URL(q, base);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return undefined;
    if (u.origin !== base.origin) return undefined;
    return u.href;
  } catch {
    return undefined;
  }
}

function resolveBase(env: ConfigEnv): Config {
  const params = new URLSearchParams(env.search);
  const debug = params.get('debug') === '1' || env.isDev === true;

  const q = params.get('api');
  if (q !== null) {
    const n = q === '' ? null : normalize(q);
    if (n) {
      safe(() => env.storage?.setItem(STORAGE_KEY_API, n));
      return { apiBase: n, debug };
    }
    if (q === '') safe(() => env.storage?.removeItem(STORAGE_KEY_API)); // ?api= azzera la scelta salvata
  }
  const stored = safe(() => env.storage?.getItem(STORAGE_KEY_API) ?? null);
  if (stored) {
    const n = normalize(stored);
    if (n) return { apiBase: n, debug };
  }
  if (env.buildApiBase) {
    const n = normalize(env.buildApiBase);
    if (n) return { apiBase: n, debug };
  }
  return { apiBase: env.origin.replace(/\/+$/, ''), debug };
}

export function loadConfig(): Config {
  let storage: ConfigEnv['storage'] = null;
  try {
    storage = window.localStorage;
  } catch {
    storage = null;
  }
  const env = import.meta.env as Record<string, string | boolean | undefined>;
  return resolveConfig({
    search: window.location.search,
    origin: window.location.origin && window.location.origin !== 'null' ? window.location.origin : 'https://streamer.hooxlab.com',
    buildApiBase: typeof env.VITE_API_BASE === 'string' ? env.VITE_API_BASE : undefined,
    isDev: env.DEV === true,
    storage,
  });
}
